import { useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import {
  postAnnotationCreateSchema,
  postAnnotationUpdateSchema,
  safeParse,
  PostAnnotationCreateInput,
  PostAnnotationUpdateInput,
} from '@/lib/validation/schemas';

export type AnnotationPostType = 'bulletin' | 'loop';

export interface PostAnnotation {
  id: string;
  post_type: AnnotationPostType;
  post_id: string;
  user_id: string;
  content: string;
  metadata: Record<string, unknown>;
  is_visible: boolean;
  created_at: string;
  updated_at: string;
  has_seen?: boolean;
  first_seen_at?: string | null;
  last_seen_at?: string | null;
}

interface UsePostAnnotationsOptions {
  includeSeen?: boolean;
  enabled?: boolean;
}

export function usePostAnnotations(postId: string | undefined, postType: AnnotationPostType, options?: UsePostAnnotationsOptions) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const queryEnabled = useMemo(() => !!postId && !!user && (options?.enabled ?? true), [postId, user, options?.enabled]);

  const annotationsQuery = useQuery({
    queryKey: ['post-annotations', postType, postId, user?.id, options?.includeSeen],
    enabled: queryEnabled,
    staleTime: 60_000,
    queryFn: async (): Promise<PostAnnotation[]> => {
      if (!postId) return [];

      const { data, error } = await supabase
        .rpc('get_post_annotations', {
          p_post_type: postType,
          p_post_id: postId,
          p_include_seen: options?.includeSeen ?? false,
        });

      if (error) {
        console.error('[usePostAnnotations] Failed to fetch annotations', error);
        throw error;
      }

      const rows = (data || []) as any[];
      return rows.map((row) => ({
        id: row.id,
        post_type: row.post_type,
        post_id: row.post_id,
        user_id: row.user_id,
        content: row.content,
        metadata: row.metadata ?? {},
        is_visible: row.is_visible,
        created_at: row.created_at,
        updated_at: row.updated_at,
        has_seen: row.has_seen,
        first_seen_at: row.first_seen_at,
        last_seen_at: row.last_seen_at,
      }));
    },
  });

  const refreshAnnotations = useCallback(() => {
    if (!postId) return;
    queryClient.invalidateQueries({ queryKey: ['post-annotations', postType, postId] });
  }, [postId, postType, queryClient]);

  return {
    annotations: annotationsQuery.data ?? [],
    isLoading: annotationsQuery.isLoading,
    isError: annotationsQuery.isError,
    refetch: annotationsQuery.refetch,
    refreshAnnotations,
  };
}

export function useAnnotationMutations(postId: string | undefined, postType: AnnotationPostType) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const invalidate = useCallback(() => {
    if (!postId) return;
    queryClient.invalidateQueries({ queryKey: ['post-annotations', postType, postId] });
  }, [postId, postType, queryClient]);

  const create = useMutation({
    mutationFn: async (input: Omit<PostAnnotationCreateInput, 'postId' | 'postType'> & { content: string; metadata?: Record<string, unknown> }) => {
      if (!user?.id || !postId) {
        throw new Error('User or post missing');
      }

      const payload = {
        postId,
        postType,
        content: input.content,
        metadata: input.metadata ?? {},
      } satisfies PostAnnotationCreateInput;

      const parseResult = safeParse(postAnnotationCreateSchema, payload);
      if (!parseResult.success) {
        const message = 'error' in parseResult ? parseResult.error : 'Invalid annotation payload';
        throw new Error(message);
      }

      const { content, metadata } = parseResult.data;

      const { error } = await supabase
        .from('post_annotations')
        .insert({
          post_id: postId,
          post_type: postType,
          user_id: user.id,
          content,
          metadata: metadata ?? {},
        });

      if (error) {
        console.error('[useAnnotationMutations] Failed to create annotation', error);
        throw error;
      }
    },
    onSuccess: () => {
      invalidate();
      toast({ title: 'Annotation added', description: 'Your note is now visible on this post.' });
    },
    onError: (error) => {
      console.error('[useAnnotationMutations] create error', error);
      toast({ title: 'Failed to add annotation', description: error.message, variant: 'destructive' });
    },
  });

  const update = useMutation({
    mutationFn: async (input: { id: string } & PostAnnotationUpdateInput) => {
      const { id, ...rest } = input;
      const parseResult = safeParse(postAnnotationUpdateSchema, rest);
      if (!parseResult.success) {
        const message = 'error' in parseResult ? parseResult.error : 'Invalid annotation update';
        throw new Error(message);
      }

      const payload: Record<string, unknown> = {};
      if (parseResult.data.content !== undefined) payload.content = parseResult.data.content;
      if (parseResult.data.metadata !== undefined) payload.metadata = parseResult.data.metadata;
      if (parseResult.data.isVisible !== undefined) payload.is_visible = parseResult.data.isVisible;

      if (Object.keys(payload).length === 0) return;

      const { error } = await supabase
        .from('post_annotations')
        .update(payload)
        .eq('id', id);

      if (error) {
        console.error('[useAnnotationMutations] Failed to update annotation', error);
        throw error;
      }
    },
    onSuccess: () => {
      invalidate();
      toast({ title: 'Annotation updated', description: 'Your note changes were saved.' });
    },
    onError: (error) => {
      console.error('[useAnnotationMutations] update error', error);
      toast({ title: 'Failed to update annotation', description: error.message, variant: 'destructive' });
    },
  });

  const remove = useMutation({
    mutationFn: async (annotationId: string) => {
      const { error } = await supabase
        .from('post_annotations')
        .delete()
        .eq('id', annotationId);

      if (error) {
        console.error('[useAnnotationMutations] Failed to delete annotation', error);
        throw error;
      }
    },
    onSuccess: () => {
      invalidate();
      toast({ title: 'Annotation removed', description: 'The note has been deleted.' });
    },
    onError: (error) => {
      console.error('[useAnnotationMutations] delete error', error);
      toast({ title: 'Failed to remove annotation', description: error.message, variant: 'destructive' });
    },
  });

  const markSeen = useMutation({
    mutationFn: async (annotationId: string) => {
      const { error } = await supabase.rpc('mark_post_annotation_viewed', { p_annotation_id: annotationId });
      if (error) {
        console.error('[useAnnotationMutations] Failed to mark annotation viewed', error);
        throw error;
      }
    },
    onSuccess: () => {
      invalidate();
    },
    onError: (error) => {
      console.error('[useAnnotationMutations] markSeen error', error);
      // No toast: silent failure is fine for view tracking
    },
  });

  return {
    create,
    update,
    remove,
    markSeen,
  };
}
